import { useState } from "react";
import { icons } from "../assets/icons/icons";
import { useSound } from "../components/SoundHandler";
import { ContainerShape, HexagonShape, InnerShape } from "../components/Shape";
import Loader from "~/core/components/Loader";

interface ResetLeaderboardModalProps {
  onConfirm: () => Promise<void> | void;
  onDismiss: () => void;
}

export const ResetLeaderboardModal = ({ onConfirm: _onConfirm, onDismiss }: ResetLeaderboardModalProps) => {
  const sounds = useSound();
  const [submitting, setSubmitting] = useState(false);

  const onClose = () => {
    if (submitting) return;
    onDismiss();
    sounds.play("click");
  };

  const onConfirm = async () => {
    if (submitting) return;
    sounds.play("click");
    setSubmitting(true);
    try {
      await _onConfirm();
      onDismiss();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#18181B]/50">
      <div>
        <ContainerShape {...{ cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 }}>
          <div className="p-6 w-[350px] md:w-[448px]">
            <div className="flex flex-row justify-between">
              <div className="mt-1 text-2xl font-blinko font-black text-white">
                Start a new<br />leaderboard season?
              </div>

              <button onClick={onClose}>
                <HexagonShape cornerRadius={6} strokeWidth={1}>
                  <div className="flex justify-center items-center p-2">
                    <img src={icons.close} className="w-6 h-6" />
                  </div>
                </HexagonShape>
              </button>
            </div>

            <div className="flex flex-col gap-2 mt-4 text-sm text-white/80">
              <p>
                This snapshots every referral's current Honey as the new baseline.
                Your clan leaderboard restarts from zero from this moment on.
              </p>
              <ul className="space-y-1 list-disc pl-5 text-white/60">
                <li>Only new Honey earned from now counts toward rankings.</li>
                <li>Previous season deltas are not saved — this can't be undone.</li>
                <li>You can reset again anytime to start a fresh season.</li>
              </ul>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={onClose} disabled={submitting}>
                <div className="px-6 py-3">
                  <p className="text-sm font-blinko text-white/70">Cancel</p>
                </div>
              </button>

              <button onClick={onConfirm} disabled={submitting}>
                <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                  <div className="relative px-6 py-3 flex items-center gap-2">
                    {submitting && <Loader size="14px" stroke="#ffffff" />}
                    <p className="text-sm font-blinko text-white">
                      {submitting ? "Resetting" : "Start new season"}
                    </p>
                  </div>
                </InnerShape>
              </button>
            </div>
          </div>
        </ContainerShape>
      </div>
    </div>
  );
};
